import React from "react";
import { Box, BoxProps, Button, ButtonProps, styled, useTheme } from "@mui/material";
import clsx from "clsx";
import EditImg from "../../images/edit.png";

interface StyledTextProps extends BoxProps {
    ellipsis?: boolean;
}

interface SizedTextProps extends StyledTextProps {
    size?: string;
}

const StyledBox = styled(Box)<{ ellipsis?: number }>(({ ellipsis }) => ({
    ...(ellipsis && {
        overflow: `clip`,
        whiteSpace: `nowrap`,
        textOverflow: `ellipsis`,
    }),
}));
export const H1 = (props: StyledTextProps) => {
    const { ellipsis, children, className, ...others } = props;
    return (
        <StyledBox
            fontSize={28}
            component="h1"
            fontWeight={600}
            ellipsis={ellipsis ? 1 : 0}
            className={clsx({
                [className || ``]: true,
            })}
            {...others}
        >
            {children}
        </StyledBox>
    );
};
export const H2 = (props: SizedTextProps) => {
    const { ellipsis, children, className, size, ref, ...others } = props;
    return (
        <StyledBox
            fontSize={size && size !== `small` ? 32 : 24}
            component="h2"
            ref={ref}
            fontWeight={600}
            ellipsis={ellipsis ? 1 : 0}
            className={clsx({
                [className || ``]: true,
            })}
            {...others}
        >
            {children}
        </StyledBox>
    );
};
export const H3 = (props: StyledTextProps) => {
    const { ellipsis, children, className, ...others } = props;
    return (
        <StyledBox
            fontSize={18}
            component="h3"
            color="text.secondary"
            fontWeight={600}
            ellipsis={ellipsis ? 1 : 0}
            className={clsx({
                [className || ``]: true,
            })}
            {...others}
        >
            {children}
        </StyledBox>
    );
};
export const H4 = (props: StyledTextProps) => {
    const { ellipsis, children, className, ...others } = props;
    const theme = useTheme();
    return (
        <StyledBox
            fontSize={20}
            component="h4"
            color={theme.palette.account.text.primary}
            fontWeight={600}
            ellipsis={ellipsis ? 1 : 0}
            className={clsx({
                [className || ``]: true,
            })}
            {...others}
        >
            {children}
        </StyledBox>
    );
};
export const CapHeader = (props: SizedTextProps) => {
    const { ellipsis, children, className, size, ...others } = props;
    return (
        <StyledBox
            fontSize={size === `small` ? 17 : 16}
            component="h5"
            color="text.secondary"
            fontWeight={600}
            sx={{
                "&.edit": {
                    cursor: `url(${EditImg}), auto`,
                },
                "&.editing": {
                    cursor: `text`,
                },
            }}
            ellipsis={ellipsis ? 1 : 0}
            className={clsx({
                [className || ``]: true,
            })}
            {...others}
        >
            {children}
        </StyledBox>
    );
};
export const H5 = (props: SizedTextProps) => {
    const { ellipsis, children, className, size, ...others } = props;
    return (
        <StyledBox
            fontSize={size && size === `small` ? 18 : 20}
            component="h5"
            color="text.secondary"
            fontWeight={600}
            ellipsis={ellipsis ? 1 : 0}
            className={clsx({
                [className || ``]: true,
            })}
            {...others}
        >
            {children}
        </StyledBox>
    );
};
export const H6 = (props: StyledTextProps) => {
    const { ellipsis, children, className, ...others } = props;
    const theme = useTheme();
    return (
        <StyledBox
            fontSize={15}
            component="h6"
            fontWeight={600}
            color={theme.palette.account.text.primary}
            ellipsis={ellipsis ? 1 : 0}
            className={clsx({
                [className || ``]: true,
            })}
            {...others}
        >
            {children}
        </StyledBox>
    );
};
export const ClickableText = (props: ButtonProps) => {
    const theme = useTheme();
    const { children, sx = {}, ...others } = props;
    return (
        <Button
            sx={{
                fontSize: 15,
                fontWeight: 600,
                whiteSpace: `nowrap`,
                color: theme.palette.account.accent,
                "&:disabled": {
                    background: `none`,
                },
                ...sx,
            }}
            {...others}
        >
            {children}
        </Button>
    );
};
export const Paragraph = (props: StyledTextProps) => {
    const { ellipsis, children, className, ...others } = props;
    const theme = useTheme();
    return (
        <StyledBox
            fontSize={15}
            component="p"
            fontWeight={500}
            color={theme.palette.account.text.primary}
            ellipsis={ellipsis ? 1 : 0}
            className={clsx({
                [className || ``]: true,
            })}
            {...others}
        >
            {children}
        </StyledBox>
    );
};
export const Small = (props: StyledTextProps) => {
    const { ellipsis, children, className, ...others } = props;
    const theme = useTheme();
    return (
        <StyledBox
            fontSize={15}
            component="small"
            fontWeight={500}
            color={theme.palette.account.text.primary}
            lineHeight={1.6}
            ellipsis={ellipsis ? 1 : 0}
            className={clsx({
                [className || ``]: true,
            })}
            {...others}
        >
            {children}
        </StyledBox>
    );
};
export const Span = (props: StyledTextProps) => {
    const { ellipsis, children, className, ...others } = props;
    const theme = useTheme();
    return (
        <StyledBox
            fontSize={15}
            component="span"
            color={theme.palette.account.text.primary}
            ellipsis={ellipsis ? 1 : 0}
            className={clsx({
                [className || ``]: true,
            })}
            {...others}
        >
            {children}
        </StyledBox>
    );
};
export const Tiny = (props: SizedTextProps) => {
    const { ellipsis, children, className, sx = {}, ...others } = props;
    const theme = useTheme();
    return (
        <StyledBox
            component="p"
            fontSize={15}
            fontWeight={500}
            lineHeight={1.65}
            sx={{
                "&.edit": {
                    cursor: `url(${EditImg}), auto`,
                },
                "&.editing": {
                    cursor: `text`,
                },
                ...sx,
            }}
            color={theme.palette.account.text.primary}
            ellipsis={ellipsis ? 1 : 0}
            className={clsx({
                [className || ``]: true,
            })}
            {...others}
        >
            {children}
        </StyledBox>
    );
};

export const AccountHeader = (props: SizedTextProps) => {
    const { children, size, ref, ...others } = props;
    return (
        <H2
            ref={ref}
            mt={0}
            sx={{
                display: `inline-block`,
                padding: size === `small` ? `0 24px` : ``,
                "&.edit": {
                    cursor: `url(${EditImg}), auto`,
                },
                "&.editing": {
                    cursor: `text`,
                },
            }}
            mr={2}
            mb={3}
            size={size}
            {...others}
        >
            {children}
        </H2>
    );
};
